import React, { useState, useEffect, useRef } from 'react';
import './HomeGallery.css';

// Gallery with lightbox support. Pass `images` as array of public-relative paths.
function HomeGallery({ images = [] }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const modalRef = useRef(null);

  const openAt = (index) => {
    setCurrent(index);
    setOpen(true);
  };

  const gridRef = useRef(null);

  // Calculate and set grid row spans so items keep natural heights while
  // preserving DOM order (row-major). Uses the implicit row height from CSS.
  const resizeAll = () => {
    const grid = gridRef.current;
    if (!grid) return;
    const rowHeight = parseInt(getComputedStyle(grid).getPropertyValue('grid-auto-rows')) || 8;
    const gap = parseInt(getComputedStyle(grid).getPropertyValue('gap')) || parseInt(getComputedStyle(grid).getPropertyValue('grid-row-gap')) || 12;
    Array.from(grid.children).forEach((item) => {
      const img = item.querySelector('img');
      if (!img) return;
      const height = img.getBoundingClientRect().height;
      const span = Math.max(1, Math.round((height + gap) / (rowHeight + gap)));
      item.style.gridRowEnd = `span ${span}`;
    });
  };

  useEffect(() => {
    // Recompute spans after images load and on resize
    const grid = gridRef.current;
    if (!grid) return;
    const imgs = grid.querySelectorAll('img');
    let loaded = 0;
    const onImgLoad = () => {
      loaded += 1;
      if (loaded === imgs.length) setTimeout(resizeAll, 50);
      else resizeAll();
    };
    imgs.forEach((img) => {
      if (img.complete) {
        // already loaded
        onImgLoad();
      } else {
        img.addEventListener('load', onImgLoad);
        img.addEventListener('error', onImgLoad);
      }
    });

    const onResize = () => resizeAll();
    window.addEventListener('resize', onResize);
    // initial pass
    setTimeout(resizeAll, 60);

    return () => {
      imgs.forEach((img) => {
        img.removeEventListener('load', onImgLoad);
        img.removeEventListener('error', onImgLoad);
      });
      window.removeEventListener('resize', onResize);
    };
  }, [images, open]);

  const close = () => {
    setOpen(false);
    // exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const prev = (e) => {
    if (e) e.stopPropagation();
    setCurrent((c) => (c - 1 + images.length) % images.length);
  };

  const next = (e) => {
    if (e) e.stopPropagation();
    setCurrent((c) => (c + 1) % images.length);
  };

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        setOpen(false);
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      }
      if (e.key === 'ArrowLeft') {
        setCurrent((c) => (c - 1 + images.length) % images.length);
      }
      if (e.key === 'ArrowRight') {
        setCurrent((c) => (c + 1) % images.length);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, images.length]);

  return (
    <section className="home-gallery">
      <div className="home-gallery-grid" ref={gridRef}>
        {images.map((src, i) => (
          <div key={i} className="home-gallery-item" onClick={() => openAt(i)}>
            <img loading="lazy" src={src.startsWith('/') ? src : `/${src}`} alt={`gallery-${i}`} />
          </div>
        ))}
      </div>

      {open && (
        <div className="home-gallery-modal" onClick={close} ref={modalRef}>
          <button className="hg-close" onClick={(e) => { e.stopPropagation(); close(); }} aria-label="Close">✕</button>
          <button className="hg-prev" onClick={(e) => prev(e)} aria-label="Previous">‹</button>
          <div className="hg-image-wrap" onClick={(e) => e.stopPropagation()}>
            <img src={images[current].startsWith('/') ? images[current] : `/${images[current]}`} alt={`gallery-${current}`} />
          </div>
          <button className="hg-next" onClick={(e) => next(e)} aria-label="Next">›</button>
        </div>
      )}
    </section>
  );
}

export default HomeGallery;
